/** POS Sales PDF exports — print window → Save as PDF */
import { resolveBranchForPrint } from './branchPrint';

const escapeHtml = (value) =>
  String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;');

const formatDate = (dateString) => {
  if (!dateString) return '';
  return new Date(dateString).toLocaleDateString('en-GB', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric'
  });
};

const formatMoney = (amount) =>
  `Rs. ${Number(amount || 0).toLocaleString('en-LK', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  })}`;

/** Build readable items summary: "Laptop x1; Mouse x2" */
export const formatPosItemsSummary = (items = []) => {
  if (!Array.isArray(items) || items.length === 0) return '—';
  return items
    .map((item) => {
      const name = item.product_name || item.name || item.product_code || 'Item';
      const qty = parseInt(item.quantity, 10) || 1;
      return `${name} x${qty}`;
    })
    .join('; ');
};

const sumMethod = (rows, method) =>
  rows
    .filter((tx) => {
      if (String(tx.status || '').toLowerCase() === 'void' || tx.is_refund) return false;
      if (method === 'online') {
        return ['online', 'mobile', 'bank_transfer'].includes(tx.payment_method);
      }
      return tx.payment_method === method;
    })
    .reduce((s, tx) => s + (parseFloat(tx.total_amount) || 0), 0);

const openPrintWindow = (title, bodyHtml) => {
  const win = window.open('', '_blank', 'width=1100,height=800,scrollbars=yes,resizable=yes');
  if (!win) {
    alert('Please allow pop-ups to export PDF');
    return;
  }
  const baseUrl = window.location.origin;
  const logoUrl = `${baseUrl}/LOGOFile.png`;

  win.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
<title>${escapeHtml(title)}</title>
<base href="${baseUrl}/">
<style>
  @import url('https://fonts.googleapis.com/css2?family=Poppins:wght@400;500;600;700&family=Roboto+Mono:wght@500&display=swap');
  * { box-sizing: border-box; }
  body {
    font-family: 'Poppins', Arial, sans-serif;
    font-size: 11px;
    color: #222;
    margin: 0;
    padding: 18px 22px;
    background: #fff;
  }
  .sheet { max-width: 210mm; margin: 0 auto; }
  .header {
    display: flex; align-items: center; justify-content: space-between;
    padding-bottom: 10px; margin-bottom: 12px;
    border-bottom: 2px solid #1976d2;
  }
  .company-logo { height: 72px; width: auto; max-width: 180px; object-fit: contain; }
  .company-info { text-align: right; }
  .company-name { font-size: 13px; font-weight: 700; color: #1976d2; }
  .store-info { font-size: 10px; color: #666; margin-top: 3px; line-height: 1.35; }
  .doc-banner {
    display: flex; justify-content: space-between; align-items: flex-start; gap: 12px;
    background: linear-gradient(90deg, #e3f2fd 0%, #f5f9fc 100%);
    border: 1px solid #bbdefb; border-radius: 6px;
    padding: 10px 14px; margin-bottom: 12px;
  }
  .doc-title { font-size: 16px; font-weight: 700; color: #1565c0; margin: 0 0 4px; }
  .doc-meta { color: #555; font-size: 10px; line-height: 1.4; }
  .branch-badge {
    background: #1976d2; color: #fff; font-weight: 600; font-size: 11px;
    padding: 6px 12px; border-radius: 20px; white-space: nowrap;
  }
  .branch-code { opacity: 0.9; font-family: 'Roboto Mono', monospace; font-size: 10px; }
  .summary-grid {
    display: grid; grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
    gap: 8px; margin-bottom: 14px;
  }
  .summary-card {
    border: 1px solid #e0e0e0; border-radius: 6px; padding: 8px 10px;
    background: #fafafa;
  }
  .summary-card .label { font-size: 9px; color: #666; text-transform: uppercase; letter-spacing: 0.3px; }
  .summary-card .value { font-size: 12px; font-weight: 700; color: #222; margin-top: 2px; }
  .summary-card.net { background: #e8f5e9; border-color: #a5d6a7; }
  .summary-card.net .value { color: #2e7d32; }
  table { width: 100%; border-collapse: collapse; margin-top: 4px; }
  th {
    background: #1976d2; color: #fff; text-align: left;
    padding: 7px 8px; font-size: 10px; font-weight: 600;
  }
  td {
    border-bottom: 1px solid #eee; padding: 6px 8px; vertical-align: top; font-size: 10px;
  }
  tr:nth-child(even) td { background: #f8fbff; }
  td.items { max-width: 220px; word-break: break-word; color: #444; }
  .right { text-align: right; }
  tfoot td {
    background: #e3f2fd !important; font-weight: 700; border-top: 2px solid #1976d2;
    padding: 8px;
  }
  .footer {
    margin-top: 16px; padding-top: 10px; border-top: 1px dashed #ccc;
    text-align: center; color: #777; font-size: 10px; line-height: 1.4;
  }
  .actions { margin-top: 18px; }
  .actions button {
    background: #1976d2; color: #fff; border: none; border-radius: 4px;
    padding: 8px 16px; font-weight: 600; cursor: pointer; margin-right: 8px;
  }
  .actions button.secondary { background: #607d8b; }
  @media print {
    body { padding: 8mm; }
    .no-print { display: none !important; }
    tr:nth-child(even) td { background: transparent; }
  }
</style></head><body>
<div class="sheet">
  <div class="header">
    <img src="${logoUrl}" alt="Logo" class="company-logo" onerror="this.style.display='none'">
    <div class="company-info">
      <div class="company-name">IT Professionals Since 1997</div>
      <div class="store-info">
        1118, Pannipitiya Road, Wickramasinghepura, Thalawathugoda<br>
        Tel: 011 27 96 579 | 94 77 987 1130<br>
        Email: info@sunsystems.lk | Web: sunsystems.lk
      </div>
    </div>
  </div>
  ${bodyHtml}
  <div class="footer">
    Sun System ERP · Confidential<br>
    Generated ${escapeHtml(new Date().toLocaleString('en-GB'))}
  </div>
</div>
<div class="no-print actions">
  <button onclick="window.print()">Print / Save as PDF</button>
  <button class="secondary" onclick="window.close()">Close</button>
</div>
<script>window.onload = function(){ setTimeout(function(){ window.print(); }, 350); };</script>
</body></html>`);
  win.document.close();
};

const branchBannerHtml = (title, metaLines, branch) => `
  <div class="doc-banner">
    <div>
      <h1 class="doc-title">${escapeHtml(title)}</h1>
      <div class="doc-meta">${metaLines.map((l) => escapeHtml(l)).join('<br>')}</div>
    </div>
    <div class="branch-badge">
      ${escapeHtml(branch.name || branch.label || 'All Branches')}
      ${branch.code ? `<div class="branch-code">${escapeHtml(branch.code)}</div>` : ''}
    </div>
  </div>`;

/**
 * Export current (filtered) POS sales list as PDF.
 */
export const exportPosSalesPdf = (transactions = [], options = {}) => {
  const title = options.title || 'POS Sales Report';
  const branch = resolveBranchForPrint(options);
  const counted = transactions.filter(
    (tx) => !tx.is_refund && String(tx.status || '').toLowerCase() !== 'void'
  );

  const rows = transactions.map((tx) => {
    const statusLabel = options.getStatusLabel
      ? options.getStatusLabel(tx)
      : (tx.status || '').toUpperCase();
    return `
      <tr>
        <td>${escapeHtml(tx.pos_number || '')}</td>
        <td>${escapeHtml(formatDate(tx.transaction_date))}</td>
        <td>${escapeHtml(tx.customer_name || 'Walk-in')}</td>
        <td>${escapeHtml(tx.user_name || tx.cashier_name || '—')}</td>
        <td class="items">${escapeHtml(formatPosItemsSummary(tx.items))}</td>
        <td class="right">${escapeHtml(formatMoney(tx.total_amount))}</td>
        <td>${escapeHtml((tx.payment_method || '').toUpperCase())}</td>
        <td>${escapeHtml(statusLabel)}</td>
      </tr>`;
  }).join('');

  const total = counted.reduce((s, tx) => s + (parseFloat(tx.total_amount) || 0), 0);
  const refunds = transactions
    .filter((tx) => tx.is_refund)
    .reduce((s, tx) => s + Math.abs(parseFloat(tx.total_amount) || 0), 0);

  const breakdown = {
    cash: sumMethod(transactions, 'cash'),
    card: sumMethod(transactions, 'card'),
    credit: sumMethod(transactions, 'credit'),
    cheque: sumMethod(transactions, 'cheque'),
    online: sumMethod(transactions, 'online')
  };
  const net = total - refunds;

  openPrintWindow(title, `
    ${branchBannerHtml(title, [
      `Records: ${transactions.length} · Counted sales: ${counted.length}`,
      `Branch: ${branch.label}`
    ], branch)}
    <div class="summary-grid">
      <div class="summary-card"><div class="label">Cash</div><div class="value">${escapeHtml(formatMoney(breakdown.cash))}</div></div>
      <div class="summary-card"><div class="label">Card</div><div class="value">${escapeHtml(formatMoney(breakdown.card))}</div></div>
      <div class="summary-card"><div class="label">Credit</div><div class="value">${escapeHtml(formatMoney(breakdown.credit))}</div></div>
      <div class="summary-card"><div class="label">Cheque</div><div class="value">${escapeHtml(formatMoney(breakdown.cheque))}</div></div>
      <div class="summary-card"><div class="label">Online / Bank</div><div class="value">${escapeHtml(formatMoney(breakdown.online))}</div></div>
      <div class="summary-card"><div class="label">Refunds</div><div class="value">${escapeHtml(formatMoney(refunds))}</div></div>
      <div class="summary-card net"><div class="label">Net Total</div><div class="value">${escapeHtml(formatMoney(net))}</div></div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Receipt #</th>
          <th>Date</th>
          <th>Customer</th>
          <th>Cashier</th>
          <th>Items</th>
          <th class="right">Amount</th>
          <th>Payment</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="8">No records</td></tr>'}</tbody>
      <tfoot>
        <tr>
          <td colspan="5">Total (excl. voids / refunds)</td>
          <td class="right">${escapeHtml(formatMoney(total))}</td>
          <td colspan="2"></td>
        </tr>
      </tfoot>
    </table>
  `);
};

/**
 * Export pending payments (credit / cheque / online pending) as PDF.
 */
export const exportPendingPaymentsPdf = (transactions = [], options = {}) => {
  const title = options.title || 'Pending Payments Report';
  const branch = resolveBranchForPrint(options);
  const pending = transactions.filter((tx) => {
    if (tx.is_refund || String(tx.status || '').toLowerCase() === 'void') return false;
    if (tx.payment_method === 'credit' && tx.status === 'pending') return true;
    if (tx.payment_method === 'cheque' && tx.cheque_status === 'pending') return true;
    if (['online', 'mobile', 'bank_transfer'].includes(tx.payment_method) && tx.online_payment_status === 'pending') {
      return true;
    }
    return false;
  });

  const rows = pending.map((tx) => `
      <tr>
        <td>${escapeHtml(tx.pos_number || '')}</td>
        <td>${escapeHtml(formatDate(tx.transaction_date))}</td>
        <td>${escapeHtml(tx.customer_name || 'Walk-in')}</td>
        <td class="items">${escapeHtml(formatPosItemsSummary(tx.items))}</td>
        <td class="right">${escapeHtml(formatMoney(tx.total_amount))}</td>
        <td>${escapeHtml((tx.payment_method || '').toUpperCase())}</td>
        <td>${escapeHtml((tx.status || '').toUpperCase())}</td>
      </tr>`).join('');

  const total = pending.reduce((s, tx) => s + (parseFloat(tx.total_amount) || 0), 0);

  openPrintWindow(title, `
    ${branchBannerHtml(title, [
      `Pending records: ${pending.length}`,
      `Branch: ${branch.label}`
    ], branch)}
    <div class="summary-grid">
      <div class="summary-card net"><div class="label">Total Pending</div><div class="value">${escapeHtml(formatMoney(total))}</div></div>
    </div>
    <table>
      <thead>
        <tr>
          <th>Receipt #</th>
          <th>Date</th>
          <th>Customer</th>
          <th>Items</th>
          <th class="right">Amount</th>
          <th>Payment</th>
          <th>Status</th>
        </tr>
      </thead>
      <tbody>${rows || '<tr><td colspan="7">No pending payments</td></tr>'}</tbody>
      <tfoot>
        <tr>
          <td colspan="4">Total pending</td>
          <td class="right">${escapeHtml(formatMoney(total))}</td>
          <td colspan="2"></td>
        </tr>
      </tfoot>
    </table>
  `);
};
